import { z } from 'zod';
import { sectionHasValue } from '../util';
import { baseBloodPressureRefined } from '../oasis-sections/vitals';
import { basePainRefined } from '../shared/pain';
import { baseMusculoskeletalRefined } from '../oasis-sections/section-g';
import { baseGenitourinarySchema } from '../shared/genitourinary';
import { basePsychosocialSchemaRefined } from '../shared/psychosocial';
import { baseNeurologicalSchemaRefined } from '../shared/neurological';
import { baseIntegumentaryRefined } from '../shared/integumentary';
import { baseCardiovascularSchemaRefined } from '../shared/cardiovascular';
import { baseGastrointestinalSchema } from '../shared/gastrointestinal';
import { baseLungAuscultationSchemaRefined, baseBreathingPatternSchema, baseCpapBipapSchema, baseMechanicalVentilatorSchema, baseTracheostomySchema, baseRespiratoryTreatmentsSchema, baseSmokingHistorySchema, baseCoughSchema, } from '../shared/respiratory';
import { baseTemperatureRefined, baseO2Refined, baseRespirationRefined, basePulseRefined, baseBloodSugarRefined } from '../shared/vitals';
import { baseEndocrineSchema } from '../shared/endocrine';
import { baseHealthManagementSchema } from '../shared/health-management';
import { baseOstomySchemaRefined } from '../shared/ostomy';
import { basePainAssessmentSchema } from '../shared/pain-assessment';
import { baseLabDrawSchema } from '../shared/labDraw';
import { baseOralIntakeClinical, baseEatingSupport, basePocNutrition, baseK0520Clinical, baseWeightClinicalRefined, } from '../shared/nutrition';
export const AI_NARRATIVE_PENDING_SENTINEL = '__AI_NARRATIVE_PENDING__';
export const AI_NARRATIVE_AWAITING_REVIEW_SENTINEL = '__AI_NARRATIVE_AWAITING_REVIEW__';
const AI_NARRATIVE_SENTINELS = [AI_NARRATIVE_PENDING_SENTINEL, AI_NARRATIVE_AWAITING_REVIEW_SENTINEL];
export function isAiNarrativeSentinel(value) {
    return typeof value === 'string' && AI_NARRATIVE_SENTINELS.includes(value);
}
/**
 * Permissive base — all fields optional. Used by chart-context extractors and
 * text generators which must tolerate partial drafts. The strict form-save
 * version (with `.min(1, 'required')` on the four required fields) is applied
 * at the composition site below so form validation still enforces required
 * fields without leaking those constraints into consumer code.
 */
export const baseHomeboundReason = z.object({
    homeboundStatusThisVisit: z.string().optional(),
    whatChanged: z.array(z.string()).optional(),
    dateChangeNoticed: z.string().optional(),
    triggerEvent: z.string().optional(),
    changeDescription: z.string().optional(),
    lastDayMetCriteria: z.string().optional(),
    reasonNoLongerHomebound: z.string().optional(),
    clinicalJustification: z.string().optional(),
    activeQualifyingConditions: z.array(z.string()).optional(),
    activeQualifyingConditionsOther: z.string().optional(),
    activeMobilityStatus: z.array(z.string()).optional(),
});
/**
 * Permissive base — all sub-sections optional. Used by chart-context extractors
 * and the vitals text generator which must tolerate partial drafts. The
 * strict form-save version (with all sub-sections required and refined) is
 * applied at the composition site below.
 */
export const baseSnnVitals = z.object({
    bloodPressure: baseBloodPressureRefined.optional(),
    pulse: basePulseRefined.optional(),
    respiration: baseRespirationRefined.optional(),
    o2: baseO2Refined.optional(),
    temperature: baseTemperatureRefined.optional(),
    bloodSugar: baseBloodSugarRefined.optional(),
});
/**
 * Permissive base — all fields optional. The form-save composition below
 * applies the `.default('yes')` for `patientPresentDuringVisit`; the base
 * version stays optional so chart-context / text-generator consumers don't
 * have to satisfy a defaulted required field.
 */
export const baseSnnSubjectiveReport = z.object({
    chiefComplaint: z.string().optional(),
    changeStatus: z.string().optional(),
    changeDetails: z.string().optional(),
    primaryConditionStatus: z.string().optional(),
    patientComplianceStatus: z.string().optional(),
    patientPresentDuringVisit: z.string().optional(),
    caregiverPresent: z.string().optional(),
});
export const baseSnnCoordinationOfCare = z.object({
    physicianNotified: z.array(z.string()).optional(),
    nextVisitPlans: z.string().optional(),
    coordinationDiscipline: z.array(z.string()).optional(),
    coordinationName: z
        .object({
        md: z.string().optional(),
        np: z.string().optional(),
        pa: z.string().optional(),
        sn: z.string().optional(),
        lpn: z.string().optional(),
        pt: z.string().optional(),
        ot: z.string().optional(),
        slp: z.string().optional(),
        hha: z.string().optional(),
    })
        .optional(),
    narrative: z.string().optional(),
});
export const snnValidationSchema = z.object({
    id: z.string().optional(),
    data: z.object({
        SubjectiveReport: baseSnnSubjectiveReport
            .extend({ patientPresentDuringVisit: z.string().default('yes') })
            .superRefine((data, ctx) => {
            if ((data.changeStatus === 'worse' || data.changeStatus === 'emergency-visit') && !data.changeDetails?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDetails'],
                });
            }
        }),
        ActiveDiagnoses: z.boolean().nullish(),
        HomeboundReason: baseHomeboundReason
            .extend({
            homeboundStatusThisVisit: z.string().min(1, 'required'),
        })
            .superRefine((data, ctx) => {
            if (data.homeboundStatusThisVisit === 'changed' && !data.changeDescription?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDescription'],
                });
            }
        }),
        Vitals: baseSnnVitals.extend({
            bloodPressure: baseBloodPressureRefined,
            pulse: basePulseRefined,
            respiration: baseRespirationRefined,
            temperature: baseTemperatureRefined,
            o2: baseO2Refined,
            pain: basePainRefined,
            bloodSugar: baseBloodSugarRefined,
        }),
        Nutrition: z.object({
            weightClinical: baseWeightClinicalRefined,
            oralIntakeClinical: baseOralIntakeClinical.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            eatingSupport: baseEatingSupport.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pocNutrition: basePocNutrition,
            nutritionalApproaches: z.array(z.string()).optional(),
            nutritionalApproachesClinical: baseK0520Clinical.optional(),
        }),
        Neurosensory: baseNeurologicalSchemaRefined.extend({
            painAssessment: basePainAssessmentSchema,
            psychosocial: basePsychosocialSchemaRefined,
        }),
        Cardiovascular: z.object({
            cardiovascular: baseCardiovascularSchemaRefined,
        }),
        Respiratory: z.object({
            smokingHistory: baseSmokingHistorySchema,
            breathingPattern: baseBreathingPatternSchema,
            lungAuscultation: baseLungAuscultationSchemaRefined,
            cough: baseCoughSchema,
            cpapBipap: baseCpapBipapSchema,
            mechanicalVentilator: baseMechanicalVentilatorSchema,
            tracheostomy: baseTracheostomySchema,
            treatments: baseRespiratoryTreatmentsSchema,
        }),
        Endocrine: z.object({
            endocrine: baseEndocrineSchema.extend({ changeFromLastVisit: z.string().min(1, 'required') }),
        }),
        BladderBowel: z.object({
            gastrointestinal: baseGastrointestinalSchema,
            genitourinary: baseGenitourinarySchema,
        }),
        Musculoskeletal: z.object({
            musculoskeletal: baseMusculoskeletalRefined,
        }),
        HealthManagement: z.object({
            healthManagement: baseHealthManagementSchema,
        }),
        SkinConditions: z.object({
            integumentary: baseIntegumentaryRefined,
        }),
        Ostomy: z.object({
            ostomy: baseOstomySchemaRefined,
        }),
        Labdraw: z.object({
            labdraw: baseLabDrawSchema,
        }),
        Infections: z.boolean().nullish(),
        Incidents: z.boolean().nullish(),
        Medication: z.boolean().nullish(),
        Supplies: z.boolean().nullish(),
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        CoordinationOfCare: baseSnnCoordinationOfCare.extend({
            narrative: z
                .string()
                .optional()
                .refine((v) => v !== AI_NARRATIVE_PENDING_SENTINEL, {
                message: 'AI narrative is generating. Wait for it to finish before saving.',
            })
                .refine((v) => v !== AI_NARRATIVE_AWAITING_REVIEW_SENTINEL, {
                message: 'AI-generated narrative is awaiting review. Accept or discard before saving.',
            }),
        }),
    }),
    signedUserId: z.string().min(1, 'required'),
    signedUserDate: z.string().min(1, 'required'),
});
